import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { apiStore } from '@/stores/ApiStore';
import { ROUTES } from '@/constants/routes';
import { LoadingSpinner } from '@/components/ui';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = observer(({ children }: ProtectedRouteProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Sync tokens from localStorage first
        apiStore.syncFromStorage();

        // Check if there's a handoff code in URL
        const code = searchParams.get('code');
        if (code && !apiStore.isAuthenticated) {
          try {
            await apiStore.loginWithHandoff(code);
          }
          catch (error) {
            console.error('Handoff login failed:', error);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }
        }

        if (!apiStore.isAuthenticated) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Verify token by calling /me endpoint only if user data is not available
        if (!apiStore.user) {
          try {
            await apiStore.getMe();
          }
          catch (error) {
            console.error('Failed to get user:', error);
            if (!apiStore.isAuthenticated) {
              setIsAuthenticated(false);
              setIsLoading(false);
              return;
            }
          }
        }

        setIsAuthenticated(true);
      }
      catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      }
      finally {
        setIsLoading(false);
      }
    };

    void checkAuth();
  }, [location.pathname, searchParams]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  return <>{children}</>;
});
